import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useCampaignProjectLabels } from 'modules/campaigns/editor/hooks';
import { Workflow } from 'modules/workflows/types';
import { WorkflowType } from 'modules/workflows/enums';
import { createWorkflowDraftMutation } from 'api/mutations/workflows';
import { NEW_WORKFLOW_ID, WORKFLOW_TRIGGERS } from 'modules/workflows/constants';
import { getNewWorkflowDraftData } from 'modules/workflows';
import { FolderId } from 'modules/domains/foldering';
import { JourneyConversion } from 'modules/journey/components/JourneyConversions';
import { getJourneyLabels } from 'modules/journey/components/SettingsTakeover/SettingsTakeover.utils';
import { hydrateJourneyGoalConversion } from '../utils/hydrateJourneyGoalConversion';

type GraphQLCreateDraftVariables = {
  workflow: Partial<Workflow>;
};

type GraphQLCreateDraftMutationResponse = {
  createWorkflowDraft: {
    id: number;
    workflowType: string;
  };
};

const displayText = {
  loading: 'Creating draft journey...',
  failure: 'Could not create draft journey',
};

const startNodeOverrides = {
  nodeClass: WORKFLOW_TRIGGERS.TRIGGERED_CUSTOM_EVENT,
  eventName: '',
};

export const useCreateNewJourney = () => {
  const dispatch = useDispatch();
  const { results: projectLabels } = useCampaignProjectLabels();

  const [createDraft] = useMutation<
    GraphQLCreateDraftMutationResponse,
    GraphQLCreateDraftVariables
  >(createWorkflowDraftMutation, {
    onError: () =>
      dispatch(addNegativeToastWithErrorId(displayText.failure, 'useCreateNewJourney')),
  });

  return async ({
    name,
    description,
    destinationFolderId,
    conversions,
    labels,
  }: {
    name: string;
    description: string | undefined;
    destinationFolderId: FolderId;
    conversions: JourneyConversion[];
    labels?: string[];
  }) => {
    try {
      dispatch(addPositiveToast(displayText.loading));

      const goals = hydrateJourneyGoalConversion(conversions?.[0]);

      const workflow = {
        ...getNewWorkflowDraftData(null, startNodeOverrides).data.workflow,
        name,
        description,
        goals,
        workflowSimultaneousLimit: 1, // default to 1 during creation
        labelIds: getJourneyLabels(labels || [], projectLabels),
      };
      workflow.nodes[0].workflowId = NEW_WORKFLOW_ID;
      // @ts-expect-error
      workflow.destinationFolderId = destinationFolderId;

      const { data } = await createDraft({
        variables: { workflow: { json: workflow } } as GraphQLCreateDraftVariables,
      });
      const id = data?.createWorkflowDraft?.id;
      const workflowType = data?.createWorkflowDraft?.workflowType as WorkflowType;
      window.location.href = `/workflows/${id}/edit?workflowType=${workflowType}`;
    } catch (error) {
      dispatch(addNegativeToastWithErrorId(displayText.failure, 'useCreateNewJourney'));
    }
  };
};
